<script setup lang="ts">
import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";
import type { MultiSelectPassThroughOptions } from "primevue/multiselect";

import { cn } from "@voltalis/frontend-utils";
import { SectionApplianceIcon } from "@voltalis/ui-kit/components";
import { computed } from "vue";

import {
  getAvailableApplianceFilters,
  ISiteSectionApplianceFilterOption,
} from "@/app/tools/new-section-management/site-sections-tree/filters/section-tree-filtering";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import MultiSelect from "primevue/multiselect";

type TSiteSectionsTreeApplianceCategoryFilterOption = ISiteSectionApplianceFilterOption & {
  label: string;
};

const props = defineProps<{
  siteSectionsTree?: IVirtualizedTreeNode<TSiteSectionsTreeData>;
}>();

const selectedCategories = defineModel<EProgramApplianceCategory[]>("selectedCategories", { required: true });

const { t, k } = useTypedIntl();

const hasSelectedCategories = computed<boolean>(() => selectedCategories.value.length > 0);
const options = computed<TSiteSectionsTreeApplianceCategoryFilterOption[]>(() => {
  return getAvailableApplianceFilters(props.siteSectionsTree).map(({ category, count }) => ({
    category,
    count,
    label: t(k.global.applianceCategory[category]),
  }));
});

const multiSelectPassThroughOptions: MultiSelectPassThroughOptions = {
  overlay: {
    "data-test-id": DATA_TEST_IDS.components.siteSectionsTreeApplianceCategoryFilter.popover,
  },
  pcFilter: {
    root: {
      "data-test-id": DATA_TEST_IDS.components.siteSectionsTreeApplianceCategoryFilter.searchInput,
    },
  },
};
</script>

<template>
  <MultiSelect
    v-model="selectedCategories"
    :class="
      cn('site-sections-tree-appliance-category-filter w-[230px]', {
        '!bg-blue-100/30 !border-blue-200': hasSelectedCategories,
      })
    "
    option-label="label"
    option-value="category"
    filter
    :show-toggle-all="false"
    :options="options"
    :placeholder="t(k.tools.siteManagement.siteTreeStructure.equipmentTypeFilter)"
    :filter-placeholder="t(k.tools.siteManagement.siteTreeStructure.searchEquipmentType)"
    :empty-filter-message="t(k.global.misc.noResult)"
    :empty-message="t(k.global.misc.noResult)"
    :data-test-id="DATA_TEST_IDS.components.siteSectionsTreeApplianceCategoryFilter.trigger"
    :pt="multiSelectPassThroughOptions"
  >
    <template #value>
      <span
        :class="
          cn('truncate text-sm font-normal leading-normal text-text-secondary', {
            'text-brand font-semibold': hasSelectedCategories,
          })
        "
        :data-test-id="DATA_TEST_IDS.components.siteSectionsTreeApplianceCategoryFilter.triggerLabel"
      >
        {{ t(k.tools.siteManagement.siteTreeStructure.equipmentTypeFilter) }}
        <span
          v-if="hasSelectedCategories"
          class="pl-1 font-semibold"
          >({{ selectedCategories.length }})</span
        >
      </span>
    </template>

    <template #option="{ option }: { option: TSiteSectionsTreeApplianceCategoryFilterOption }">
      <div
        class="flex min-w-0 items-center gap-2"
        :data-test-id="DATA_TEST_IDS.components.siteSectionsTreeApplianceCategoryFilter.option(option.category)"
      >
        <SectionApplianceIcon
          class="site-sections-tree-appliance-category-filter-icon shrink-0"
          size="small"
          :section-appliance-category="option.category"
          :data-test-id="DATA_TEST_IDS.components.siteSectionsTreeApplianceCategoryFilter.optionIcon(option.category)"
        />
        <span class="min-w-0 flex-1 truncate text-sm font-semibold">{{ option.label }}</span>
      </div>
    </template>

    <template #dropdownicon>
      <i class="pi pi-chevron-down" />
    </template>
  </MultiSelect>
</template>
